import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { ContentSection, H2, H3, Link } from '../components/ui'
import { SEO } from '../components/SEO/SEO'

export const TermsOfService = () => {
  const seoConfig = {
    title: 'Terms of Service - Et Ai Labs Legal Agreement',
    description:
      'Et Ai Labs terms of service. Read our legal agreement for using the Et Ai academic research assistant and AI-powered scientific research platform.',
    keywords:
      'Et Ai terms of service, legal agreement, AI research platform terms, academic tool terms',
    url: 'https://etai.app/terms',
    canonical: 'https://etai.app/terms',
    type: 'article',
    publishedTime: '2025-07-02',
    modifiedTime: '2025-07-02',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service',
      description: 'Et Ai Labs terms of service and legal agreement',
      publisher: {
        '@type': 'Organization',
        name: 'Et Ai Labs',
        url: 'https://etai.app',
      },
    },
  }

  return (
    <StaticPageLayout title="Terms of Service">
      <SEO {...seoConfig} />
      <ContentSection subtitle="Last updated: July 2, 2025">
        <section className="mb-8">
          <H2>1. Agreement to Terms</H2>
          <p className="text-gray-300 mb-4">
            Welcome to Et Ai. These Terms of Service (&quot;Terms&quot;) constitute a legally
            binding agreement between you and <strong>Et Ai Labs Pty Ltd</strong> (&quot;Et Ai
            Labs,&quot; &quot;Et Ai,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;).
          </p>
          <p className="text-gray-300 mb-4">
            By creating an account, accessing, or using our AI-powered research platform, website,
            and related services (collectively, the &quot;Services&quot;), you agree to be bound by
            these Terms and our <Link to="/privacy-policy">Privacy Policy</Link>. If you do not
            agree to these Terms, you may not use our Services.
          </p>
          <p className="text-gray-300 mb-4">
            If you are using the Services on behalf of an organization or entity
            (&quot;Organization&quot;), you represent and warrant that you have the authority to
            bind that Organization to these Terms.
          </p>
        </section>

        <section className="mb-8">
          <H2>2. Our Services</H2>
          <p className="text-gray-300 mb-4">
            Et Ai provides an AI-powered research platform designed to synthesize scientific
            literature, check facts, and identify research gaps. The Services are provided on a
            subscription basis and include different tiers (e.g., &quot;Free,&quot; &quot;Pro,&quot;
            &quot;Team&quot;) with varying features and usage limits.
          </p>
          <p className="text-gray-300 mb-4">
            We are constantly evolving our Services. We reserve the right to modify, add, or
            discontinue features or any part of the Service at any time, with or without notice.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">
            3. User Accounts and Responsibilities
          </h2>
          <p className="text-gray-300 mb-4">
            To use our Services, you must create an account. You agree to:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>Provide accurate, current, and complete information during registration.</li>
            <li>Keep your account password confidential and secure.</li>
            <li>Be solely responsible for all activities that occur under your account.</li>
            <li>
              Notify us immediately at <code>legal@etai.app</code> of any unauthorized use of your
              account.
            </li>
          </ul>
          <p className="text-gray-300 mb-4">
            You must be at least 16 years old to use the Services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">
            4. Subscriptions, Payments, and Cancellation
          </h2>
          <H3 className="mt-6">a) Subscription Plans</H3>
          <p className="text-gray-300 mb-4">
            We offer both free and paid subscription plans. Features and usage limits for each plan
            are described on our pricing page.
          </p>

          <H3 className="mt-6">b) Billing</H3>
          <p className="text-gray-300 mb-4">
            Paid subscriptions are billed in advance on a recurring basis (e.g., monthly or
            annually). By providing a payment method, you authorize us (through our third-party
            payment processor, <strong>Stripe</strong>) to charge the subscription fees to your
            payment method.
          </p>

          <H3 className="mt-6">c) Automatic Renewal</H3>
          <p className="text-gray-300 mb-4">
            Your paid subscription will automatically renew at the end of each billing cycle unless
            you cancel it.
          </p>

          <H3 className="mt-6">d) Cancellation</H3>
          <p className="text-gray-300 mb-4">
            You may cancel your subscription at any time through your account settings. The
            cancellation will take effect at the end of the current billing period.{' '}
            <strong>You will not receive a refund for any partial subscription periods.</strong> You
            will retain access to the paid features until the end of your billing cycle.
          </p>

          <H3 className="mt-6">e) Fee Changes</H3>
          <p className="text-gray-300 mb-4">
            We reserve the right to change our subscription fees. We will provide you with at least
            30 days&apos; advance notice of any fee changes. Your continued use of the Service after
            the fee change constitutes your agreement to pay the modified fee.
          </p>
        </section>

        <section className="mb-8">
          <H2>5. Content and Intellectual Property</H2>
          <p className="text-gray-300 mb-4">
            This is a critical section. We want to be clear about who owns what.
          </p>
          <H3 className="mt-6">a) Our Intellectual Property</H3>
          <p className="text-gray-300 mb-4">
            The Services – including the platform, software, website, graphics, branding, and our
            proprietary structured knowledge database – are the exclusive property of Et Ai Labs Pty
            Ltd and its licensors. Our IP is protected by copyright, trademark, and other
            intellectual property laws. You may not use our branding or trademarks without our prior
            written consent.
          </p>
          <H3 className="mt-6">b) Your Content</H3>
          <p className="text-gray-300 mb-4">
            This is the most important part:{' '}
            <strong>
              You retain full ownership of the intellectual property in the content you submit to
              the Service
            </strong>
            , including your scientific queries, text, and documents (your &quot;Content&quot;). We
            do not claim any ownership rights to your ideas or research.
          </p>
          <p className="text-gray-300 mb-4">
            To operate the platform, you grant Et Ai a limited, worldwide, non-exclusive,
            royalty-free license to use, reproduce, modify, and process your Content{' '}
            <strong>solely for the purpose of operating and providing the Services to you.</strong>{' '}
            This license is necessary for us to, for example, process your query and generate a
            synthesized answer for you. This license terminates when you delete your Content or your
            account.
          </p>
        </section>

        <section className="mb-8">
          <H2>6. Acceptable Use Policy</H2>
          <p className="text-gray-300 mb-4">You agree not to use the Services to:</p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>Conduct any illegal or fraudulent activities.</li>
            <li>Infringe on the intellectual property rights of others.</li>
            <li>Harass, abuse, or harm other individuals.</li>
            <li>Transmit viruses, malware, or any other malicious code.</li>
            <li>
              Attempt to gain unauthorized access to our systems, scrape our database, or
              reverse-engineer our technology.
            </li>
            <li>Circumvent any usage limits or restrictions of your subscription tier.</li>
            <li>Generate or disseminate content that is defamatory, obscene, or hateful.</li>
          </ul>
        </section>

        <section className="mb-8">
          <H2>7. Termination</H2>
          <p className="text-gray-300 mb-4">
            We may suspend or terminate your access to the Services immediately, without prior
            notice, if you breach these Terms. You may terminate your account at any time by
            cancelling your subscription and ceasing to use the Service.
          </p>
          <p className="text-gray-300 mb-4">
            Upon termination, your right to use the Service will cease, and we may delete your
            account and Content in accordance with our Privacy Policy and data retention procedures.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">
            8. Disclaimers and Limitation of Liability
          </h2>
          <H3 className="mt-6">a) &quot;AS IS&quot; Service</H3>
          <p className="text-gray-300 mb-4">
            THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS,
            WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED
            TO, IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.
          </p>

          <H3 className="mt-6">b) AI and Accuracy Disclaimer</H3>
          <p className="text-gray-300 mb-4">
            While our goal is to provide highly accurate, hallucination-free results, you
            acknowledge that the output is generated by complex AI systems.{' '}
            <strong>
              The Service is intended to be used as a powerful tool to aid, not replace,
              professional scientific judgment and verification.
            </strong>{' '}
            We do not warrant that the Service will be uninterrupted, completely secure, or entirely
            error-free. You are responsible for independently verifying the outputs of the Service.
          </p>

          <H3 className="mt-6">c) Limitation of Liability</H3>
          <p className="text-gray-300 mb-4">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL Et Ai LABS PTY LTD,
            ITS DIRECTORS, EMPLOYEES, OR PARTNERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL, OR PUNITIVE DAMAGES (INCLUDING, WITHOUT LIMITATION, LOSS OF PROFITS,
            DATA, OR GOODWILL) ARISING FROM YOUR USE OF THE SERVICE.
          </p>
          <p className="text-gray-300 mb-4">
            OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE
            TERMS OR YOUR USE OF THE SERVICES IS LIMITED TO THE GREATER OF (A) THE TOTAL AMOUNT OF
            FEES YOU HAVE PAID TO US IN THE 12 MONTHS PRIOR TO THE EVENT GIVING RISE TO THE CLAIM,
            OR (B) ONE HUNDRED AUSTRALIAN DOLLARS (AU$100).
          </p>
        </section>

        <section className="mb-8">
          <H2>9. Indemnification</H2>
          <p className="text-gray-300 mb-4">
            You agree to defend, indemnify, and hold harmless Et Ai Labs Pty Ltd and its employees,
            directors, and agents from and against any claims, damages, liabilities, and expenses
            (including legal fees) arising from your use of the Services or your violation of these
            Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">
            10. Governing Law and Dispute Resolution
          </h2>
          <p className="text-gray-300 mb-4">
            These Terms shall be governed by and construed in accordance with the laws of{' '}
            <strong>Queensland, Australia</strong>, without regard to its conflict of law
            principles.
          </p>
          <p className="text-gray-300 mb-4">
            Any dispute arising from these Terms shall be resolved in the state or federal courts
            located in Brisbane, Queensland, Australia.
          </p>
        </section>

        <section className="mb-8">
          <H2>11. General Provisions</H2>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>
              <strong>Changes to Terms:</strong> We reserve the right to modify these Terms at any
              time. We will provide notice of material changes by updating the &quot;Last
              updated&quot; date and, where appropriate, by email. Your continued use of the Service
              after such changes constitutes your acceptance of the new Terms.
            </li>
            <li>
              <strong>Entire Agreement:</strong> These Terms constitute the entire agreement between
              you and Et Ai Labs regarding our Service and supersede all prior agreements.
            </li>
            <li>
              <strong>Severability:</strong> If any provision of these Terms is held to be invalid
              or unenforceable, the remaining provisions will remain in full force and effect.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <H2>12. Contact Information</H2>
          <p className="text-gray-300 mb-4">
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="text-gray-300">
            <strong>Et Ai Labs Pty Ltd</strong>
            <br />
            <strong>Registered Address:</strong> 26 Lower River Terrace, South Brisbane, QLD 4101,
            Australia
            <br />
            <strong>Email:</strong> legal@etai.app
          </p>
        </section>
      </ContentSection>
    </StaticPageLayout>
  )
}
